/**
 * GIFT DETAILS
 * ---------------------------------------------------------------------------
 * The "This is a gift" checkbox on the PDP (snippets/gift-option.liquid) and
 * its drawer (snippets/gift-option-drawer.liquid).
 *
 * Flow, from the client's brief (lhune-gift-checkbox.html, 14 Oct 2026):
 *   - Ticking the box opens the drawer. The customer picks an occasion and
 *     the occasion's date; Confirm is enabled once both are picked.
 *   - Confirm closes the drawer and shows "Occasion · Date" with an Edit link.
 *   - Closing the drawer any other way (X, outside click, Escape) acts the
 *     same whichever it is: with both an occasion and a date picked the
 *     details are kept as if Confirm had been clicked; otherwise the box is
 *     unticked (or keeps what was confirmed before, when reopened from Edit).
 *     See the drawer:close handler. Keeping complete picks is not in the
 *     brief (user's call, 14 Oct 2026).
 *   - Unticking clears everything.
 *
 * Tried and removed on 14 Oct 2026: refusing an outside click or Escape until
 * both were picked, with a "please select" message. It swallowed every click
 * outside the drawer, including a newsletter popup's own buttons, so the
 * user chose to let an outside click work like the X instead.
 *
 * Nothing is sent to the cart from here. The PDP add-to-cart in
 * assets/no-color-v2.js.liquid asks window.GiftOption.getProperties() and
 * puts the result on the main product line only, next to the other
 * main-line properties, so certificate and Priority Delivery lines never
 * carry gift details.
 *
 * Properties written (the names are fixed here, not theme settings, so
 * exports and order filters keep working if the wording on the page changes):
 *   "Gift occasion"       visible — e.g. "Birthday"
 *   "Occasion date"       visible — e.g. "Nov 3 2026", the same format as
 *                         "Priority Delivery Date"
 *   "_gift_occasion_date" hidden ISO copy, "2026-11-03", for sorting/exports
 *
 * Dates: "today" is today in New York, as in assets/priority-delivery.js.liquid
 * — a shopper in Tokyo must not be offered a date one day earlier than the
 * workshop can meet. Every Date here is a local-midnight value built from
 * date parts; never toISOString(), which shifts to UTC and can change the day.
 *
 * Requires jQuery and assets/drawer-core.js.
 */
(function () {
  'use strict';

  var DRAWER_KEY = 'gift-option';
  var STORE_TZ = 'America/New_York';
  var MAX_DAYS = 365;
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
                'August', 'September', 'October', 'November', 'December'];
  var MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul',
                      'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  var $root, $drawer;
  var minDate, maxDate;

  // What is on the order once confirmed: { occasion, date } or null.
  var confirmed = null;
  // What the drawer currently shows; copied into `confirmed` on Confirm.
  var pending = { occasion: null, date: null };
  var viewYear, viewMonth;

  // ------------------------------------------------------------------ dates

  function midnight(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  function today() {
    try {
      var parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: STORE_TZ,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(new Date()).split('-');

      if (parts.length === 3) {
        var y = Number(parts[0]), m = Number(parts[1]), d = Number(parts[2]);
        if (y && m && d) return new Date(y, m - 1, d);
      }
    } catch (e) {
      // Intl with a timeZone is unavailable — fall through to device time.
    }
    return midnight(new Date());
  }

  function addDays(date, days) {
    var x = new Date(date.getTime());
    x.setDate(x.getDate() + days);
    return midnight(x);
  }

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }

  // Stored and shown: "Nov 3 2026". Always with the year, because the value
  // sits on an order long after the page that gave it context.
  function fmtDate(d) {
    return MONTHS_SHORT[d.getMonth()] + ' ' + d.getDate() + ' ' + d.getFullYear();
  }

  function isoDate(d) {
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }

  function parseIso(s) {
    var p = String(s).split('-');
    return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
  }

  // ---------------------------------------------------------------- drawer

  function el(selector) {
    return $drawer.find(selector);
  }

  function renderCalendar() {
    el('[data-gift-month]').text(MONTHS[viewMonth] + ' ' + viewYear);

    var firstDow = new Date(viewYear, viewMonth, 1).getDay();
    var daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    var html = '';
    var h, b, d;

    for (h = 0; h < DOW.length; h++) {
      html += '<div class="gift-cal__dow">' + DOW[h] + '</div>';
    }
    for (b = 0; b < firstDow; b++) {
      html += '<span class="gift-cal__day gift-cal__day--empty"></span>';
    }
    for (d = 1; d <= daysInMonth; d++) {
      var date = new Date(viewYear, viewMonth, d);
      var disabled = date < minDate || date > maxDate;
      var selected = pending.date && date.getTime() === pending.date.getTime();

      html += '<button type="button" class="gift-cal__day' +
              (selected ? ' is-selected' : '') + '"' +
              (disabled ? ' disabled' : '') +
              ' data-gift-day="' + isoDate(date) + '"' +
              ' aria-label="' + fmtDate(date) + '"' +
              (selected ? ' aria-pressed="true"' : '') + '>' + d + '</button>';
    }
    el('[data-gift-grid]').html(html);

    el('[data-gift-prev]').prop('disabled',
      viewYear < minDate.getFullYear() ||
      (viewYear === minDate.getFullYear() && viewMonth <= minDate.getMonth()));
    el('[data-gift-next]').prop('disabled',
      viewYear > maxDate.getFullYear() ||
      (viewYear === maxDate.getFullYear() && viewMonth >= maxDate.getMonth()));
  }

  /**
   * Works out the selectable range from today in New York. Called every time
   * the drawer opens, not once at page load: a tab left open past midnight
   * (New York) would otherwise keep offering an earliest date a day too early.
   *
   * The last date is at least 60 days after the first, so a large "earliest
   * date" setting cannot push every day out of range and leave the customer
   * with a drawer that can never be completed.
   */
  function refreshDates() {
    var minDays = parseInt($drawer.attr('data-gift-min-days'), 10);
    if (isNaN(minDays) || minDays < 0) minDays = 15;
    var start = today();
    minDate = addDays(start, minDays);
    maxDate = addDays(start, Math.max(MAX_DAYS, minDays + 60));

    var note = $drawer.attr('data-gift-note') || '';
    el('[data-gift-hint]').text(note.replace('[date]', fmtDate(minDate)));

    // A date picked before midnight can fall before the new earliest date.
    if (pending.date && pending.date < minDate) pending.date = null;
  }

  function isComplete() {
    return !!(pending.occasion && pending.date);
  }

  function renderConfirm() {
    el('[data-gift-confirm]').prop('disabled', !isComplete());
  }

  /** Loads `pending` into the drawer: occasion radios, calendar, button. */
  function renderDrawer() {
    el('[data-gift-occasion]').each(function () {
      this.checked = this.value === pending.occasion;
    });
    var shown = pending.date || minDate;
    viewYear = shown.getFullYear();
    viewMonth = shown.getMonth();
    renderCalendar();
    renderConfirm();
  }

  // ------------------------------------------------------------- checkbox

  function renderCheckbox() {
    var on = !!confirmed;
    $root.find('[data-gift-checkbox]').prop('checked', on);
    $root.find('[data-gift-hint-text]').prop('hidden', on);
    $root.find('[data-gift-summary]').prop('hidden', !on);
    $root.find('[data-gift-summary-text]').text(
      on ? confirmed.occasion + ' · ' + fmtDate(confirmed.date) : ''
    );
  }

  function clearAll() {
    confirmed = null;
    pending = { occasion: null, date: null };
    renderCheckbox();
  }

  function openDrawer() {
    pending = confirmed
      ? { occasion: confirmed.occasion, date: confirmed.date }
      : { occasion: null, date: null };
    refreshDates();
    renderDrawer();
    window.DrawerCore.open(DRAWER_KEY);
  }

  // ------------------------------------------------------------------ init

  function init() {
    $root = $('[data-gift-option]').first();
    $drawer = $('[data-gift-drawer]').first();
    if (!$root.length || !$drawer.length || !window.DrawerCore) return;

    refreshDates();

    // Ticking opens the drawer; unticking clears. The box shows as ticked
    // while the drawer is open and only stays ticked once confirmed.
    $root.on('change', '[data-gift-checkbox]', function () {
      if (this.checked) {
        openDrawer();
      } else {
        clearAll();
      }
    });

    // Edit carries data-drawer-btn for drawer-core's outside-click guard;
    // this handler runs first and loads the confirmed details.
    $root.on('click', '.gift-option__edit', function () {
      pending = { occasion: confirmed.occasion, date: confirmed.date };
      refreshDates();
      renderDrawer();
    });

    $drawer.on('change', '[data-gift-occasion]', function () {
      pending.occasion = this.value;
      renderConfirm();
    });

    // Marks the day in place rather than calling renderCalendar(). Rebuilding
    // the grid would remove the clicked button from the page while its click
    // is still bubbling; drawer-core's outside-click check then cannot find
    // the target inside the drawer and closes it. With no occasion picked
    // that unticked the box: found 14 Oct 2026 ("select only date … drawer
    // will be closed").
    $drawer.on('click', '[data-gift-day]', function () {
      pending.date = parseIso($(this).attr('data-gift-day'));
      el('[data-gift-day]').removeClass('is-selected').removeAttr('aria-pressed');
      $(this).addClass('is-selected').attr('aria-pressed', 'true');
      renderConfirm();
    });

    $drawer.on('click', '[data-gift-prev]', function () {
      viewMonth -= 1;
      if (viewMonth < 0) { viewMonth = 11; viewYear -= 1; }
      renderCalendar();
    });

    $drawer.on('click', '[data-gift-next]', function () {
      viewMonth += 1;
      if (viewMonth > 11) { viewMonth = 0; viewYear += 1; }
      renderCalendar();
    });

    $drawer.on('click', '[data-gift-confirm]', function () {
      if (!pending.occasion || !pending.date) return;
      confirmed = { occasion: pending.occasion, date: pending.date };
      renderCheckbox();
      window.DrawerCore.closeAll();
    });

    // Closed any other way (X, outside click, Escape, another drawer opening).
    // If an occasion and a date are both picked, keep them as if Confirm had
    // been clicked; otherwise untick, or keep what was confirmed before.
    //
    // The brief only saved on Confirm. Changed the same day (14 Oct 2026, the
    // user's call) after a test where the details never reached the cart: in
    // the theme preview Shopify's preview bar covers the Confirm button, and
    // a click on the bar closed the drawer and discarded complete picks.
    // A customer who picked both clearly meant it.
    $drawer.on('drawer:close', function () {
      if (pending.occasion && pending.date) {
        confirmed = { occasion: pending.occasion, date: pending.date };
      }
      renderCheckbox();

      // Give focus back to the checkbox, but only when it is still in the
      // drawer or nowhere (body). If another drawer or a popup took it, leave
      // it alone.
      var active = document.activeElement;
      if (active === document.body || $drawer[0].contains(active)) {
        $root.find('[data-gift-checkbox]').trigger('focus');
      }
    });

    // Keyboard and screen-reader users: focus moves into the drawer when it
    // opens (the chosen occasion, else the first), so Tab and the arrow keys
    // work on the occasions straight away instead of on the page behind.
    // Shown as an outline only for keyboard users (:focus-visible).
    $drawer.on('drawer:open', function () {
      setTimeout(function () {
        var $target = el('[data-gift-occasion]:checked').first();
        if (!$target.length) $target = el('[data-gift-occasion]').first();
        $target.trigger('focus');
      }, 50);
    });

    renderCheckbox();
  }

  window.GiftOption = {
    /** Line item properties for the main product line; {} when not a gift. */
    getProperties: function () {
      if (!confirmed) return {};
      return {
        'Gift occasion': confirmed.occasion,
        'Occasion date': fmtDate(confirmed.date),
        '_gift_occasion_date': isoDate(confirmed.date)
      };
    }
  };

  $(init);
})();
